// Import GSAP core and the ScrollTrigger plugin
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const animateParallax = document.querySelectorAll('[data-animate="parallax"]');
const siteHeader = document.querySelector('.site-header');
const siteLogo = document.querySelector('.site-logo');

// Detect touchscreen devices, but allow Windows touchscreen laptops to run animation
const isTouchScreen = (() => {
    if ("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0) {
        return !window.matchMedia("(any-pointer: fine)").matches;
    }
    return "ontouchstart" in window;
})();

// Function to check if the logo animation should run
function isDesktopOnly() {
    return window.innerWidth >= 992 && (!isTouchScreen || isTouchScreenDesktop());
}

// Function to detect tablet devices (e.g., iPad Pro and large Android tablets)
function isTabletDevice() {
    return /iPad|Android|Tablet|Macintosh/.test(navigator.userAgent) && "ontouchend" in document;
}

// Check if the device is a touchscreen desktop (exclude tablets)
function isTouchScreenDesktop() {
    return window.innerWidth >= 992 && isTouchScreen && !isTabletDevice();
}

// Set initial header class based on first block's data-color attribute
function setInitialHeaderClass() {
    const firstElementWithColor = document.querySelector('[data-color]');
    if (firstElementWithColor) {
        const color = firstElementWithColor.getAttribute('data-color');
        if (color) {
            siteHeader.classList.remove('dark', 'light');
            siteHeader.classList.add(color);
        }
    }
}

setInitialHeaderClass();

// Adds .header-fixed class to header on scroll
ScrollTrigger.create({
    start: "top top",
    onUpdate: self => {
        if (self.scroll() > 0) {
            siteHeader.classList.add('header-fixed');
        } else {
            siteHeader.classList.remove('header-fixed');
        }
    }
});

// Keep Pinned Elements for All Devices
animateParallax.forEach((block, index) => {
    gsap.to(block, {
        scrollTrigger: {
            trigger: block,
            start: 'top top',
            pin: true,
            pinSpacing: false,
            scrub: true,
            anticipatePin: 1,
            id: `parallax-${index}`,
        }
    });

    const figcaption = block.querySelector('figcaption');
    if (figcaption) {
        gsap.to(figcaption, {
            y: -500,
            ease: 'none',
            scrollTrigger: {
                trigger: block,
                start: 'top 80%',
                end: 'bottom top',
                scrub: true,
            },
        });
    }
});

// Update header color dynamically based on the data-color attribute of each panel
document.querySelectorAll('[data-color]').forEach((panel) => {
    const color = panel.getAttribute('data-color');

    if (color) {
        ScrollTrigger.create({
            trigger: panel,
            start: 'top top',
            end: 'bottom top',
            onEnter: () => {
                siteHeader.classList.remove('dark', 'light');
                siteHeader.classList.add(color);
            },
            onEnterBack: () => {
                siteHeader.classList.remove('dark', 'light');
                siteHeader.classList.add(color);
            },
        });
    }
});

// Ensure logo is always visible on load
window.addEventListener('load', () => {
    ScrollTrigger.refresh();

    // Fade in the site logo
    if (siteLogo) {
        gsap.to(siteLogo, {
            opacity: 1,
            duration: 0.8,
            ease: 'power2.out'
        });
    }
});

// Logo Animation (Only Runs on Desktop, Excludes Tablets)
if (document.body.classList.contains('home')) {
    let logoTween = null;
    let resizing = false;

    const siteLogoSvg = document.querySelector('.site-logo svg');
    const logoTrigger = document.querySelector('[data-trigger="logo"]');
    const logoPlaceholder = document.querySelector('.site-logo-placeholder');
    const logoStudio = document.querySelector('.site-logo .studio'); 

    function resetLogoStyles() {
        gsap.set(siteLogoSvg, { clearProps: 'all' });
    
        // Ensure .studio is always visible on tablets and mobile
        const showStudio = !isDesktopOnly() || isTabletDevice();
        gsap.set(logoStudio, { 
            opacity: showStudio ? 1 : 0, 
            visibility: showStudio ? 'visible' : 'hidden' 
        });
    
        siteLogoSvg.style.transform = '';
        siteLogoSvg.style.width = '';
        siteLogoSvg.style.opacity = '1';
    }    

    function calculatePositions() {
        const placeholderRect = logoPlaceholder.getBoundingClientRect();
        const initialRect = siteLogoSvg.getBoundingClientRect();

        return {
            targetX: placeholderRect.left - initialRect.left,
            targetY: placeholderRect.bottom - initialRect.bottom,
            minWidth: 250,
            maxWidth: 1800,
            initialWidth: initialRect.width
        };
    }

    function initLogoAnimation() {
        if (!isDesktopOnly() || isTabletDevice()) {
            resetLogoStyles();
            return;
        }

        function updateLogoPosition() {
            const { targetX, targetY, minWidth, maxWidth, initialWidth } = calculatePositions();

            logoTween = gsap.to(siteLogoSvg, {
                scrollTrigger: {
                    trigger: logoTrigger,
                    start: 'top bottom',
                    end: '+=90%',
                    scrub: 1,
                    anticipatePin: 1,
                    onUpdate: self => {
                        const progress = self.progress;
                        gsap.set(siteLogoSvg, {
                            x: gsap.utils.interpolate(0, targetX, progress),
                            y: gsap.utils.interpolate(0, targetY, progress),
                            width: gsap.utils.interpolate(maxWidth, minWidth, progress) + 'px',
                            transform: 'translateZ(0)',
                        });
                    },
                    onLeave: () => {
                        siteLogoSvg.classList.add('is-pinned');
                        gsap.to(logoStudio, { 
                            opacity: 1, 
                            duration: 0.5, 
                            visibility: 'visible', 
                            ease: 'power2.out' 
                        });
                    },
                    onEnterBack: () => {
                        siteLogoSvg.classList.remove('is-pinned');
                        gsap.to(logoStudio, { 
                            opacity: 0, 
                            duration: 0.3, 
                            visibility: 'hidden', 
                            ease: 'power2.out' 
                        });
                    }
                }
            });
        }

        updateLogoPosition();
    }

    function destroyLogoAnimation() {
        if (logoTween?.scrollTrigger) {
            logoTween.scrollTrigger.kill();
            logoTween.kill();
            logoTween = null;
        }
        resetLogoStyles();
    }

    function handleResize() {
        if (resizing) return;
        resizing = true;
        
        destroyLogoAnimation();
        gsap.set(siteLogoSvg, { opacity: 0.5 });

        setTimeout(() => {
            if (isDesktopOnly() && !isTabletDevice()) {
                initLogoAnimation();
            } else {
                resetLogoStyles();
            }
            ScrollTrigger.refresh(true);
            gsap.to(siteLogoSvg, { opacity: 1, duration: 0.3 });
            resizing = false;
        }, 100);
    }

    window.addEventListener('load', handleResize);
    window.addEventListener('resize', handleResize);
}
